import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DonationsFeedActions } from './actions';
import { selectMyLikes } from './selectors';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';
import { DonationsService } from '../../services/donations-feed/donations.service';

@Injectable()
export class DonationsFeedEffects {
  private actions$ = inject(Actions);
  private api = inject(DonationsService);
  private store = inject(Store);

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DonationsFeedActions.loadRequested),
      switchMap(() =>
        this.api.list().pipe(
          map((items) => DonationsFeedActions.loadSucceeded({ items })),
          catchError((err) =>
            of(
              DonationsFeedActions.loadFailed({
                error: err?.message ?? 'Load failed',
              })
            )
          )
        )
      )
    )
  );

  like$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DonationsFeedActions.likeToggledRequested),
      withLatestFrom(this.store.select(selectMyLikes)),
      switchMap(([{ id }, myLikes]) => {
        const liked = !myLikes[id]; // toggle real
        const delta: 1 | -1 = liked ? 1 : -1;

        const optimistic = DonationsFeedActions.likeOptimistic({
          id,
          delta,
          liked,
        });
        const call$ = this.api.like(id, liked).pipe(
          map((res) =>
            DonationsFeedActions.likeConfirmed({ id, likes: res.likes, liked })
          ),
          catchError(() => of(DonationsFeedActions.likeReverted({ id })))
        );

        return of(optimistic).pipe(switchMap(() => call$));
      })
    )
  );
}
